import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { SessionPolicy } from "@/lib/api-types";

/**
 * Story 15.7 — `GET /meta/session-policy`, the one real source of the idle
 * timeout the client-side timer schedules against (AD-2's "the frontend
 * defines none of its own" discipline, applied to a number instead of an
 * enum: `idle_timeout_minutes` must never be a hardcoded `15` here that
 * could silently drift from a deployment's real, env-overridden `Settings`
 * value — and it is exactly what makes VC-15.7-16's live check able to
 * override the real threshold to something short for verification, without
 * touching a second, independent frontend constant).
 *
 * **Tier A review fix round.** `IdleSessionGuard.tsx`'s own gate falls
 * through to a sensible `?? 15` default once this query definitively
 * fails, rather than disabling the idle timer for the tab's whole
 * lifetime — but that default is too *permissive* if the deployment's
 * real `idle_timeout_minutes` is actually configured shorter than 15, and
 * the global `QueryClient` (`main.tsx`) deliberately disables both
 * `retry` and `refetchOnWindowFocus`, so a transient failure while the
 * Owner is genuinely idle (no activity to trigger a heartbeat-driven
 * refetch) would otherwise strand the client on that fallback
 * indefinitely — a milder reproduction of the same "silently wrong idle
 * behaviour" bug class this whole fix round exists to close, just via a
 * different trigger. Two small, deliberately *scoped* overrides — only
 * this one query, never the global client — give it a real chance to
 * self-heal: a modest retry count for the initial failure, and a
 * `refetchOnWindowFocus` override so the Owner stepping away and back
 * (a genuinely common real trigger, independent of any in-page activity)
 * gets a fresh attempt too.
 */
export const SESSION_POLICY_QUERY_KEY = ["meta", "session-policy"] as const;

export function useSessionPolicy(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: SESSION_POLICY_QUERY_KEY,
    queryFn: () => apiFetch<SessionPolicy>("/meta/session-policy"),
    enabled: options?.enabled ?? true,
    // The idle policy essentially never changes mid-session — refetching it
    // on every window focus once it has genuinely SUCCEEDED would be a real
    // request for a number that will not have moved. `staleTime: Infinity`
    // still accomplishes that: a resolved query is never considered stale,
    // so `refetchOnWindowFocus` below only ever has anything to do while
    // this query is still in its error state (there is no successful,
    // cached data yet to treat as fresh) — it does not reintroduce a
    // repeating background request once a real value is in hand.
    staleTime: Infinity,
    // A modest, scoped override of the global client's own `retry: false`
    // — three attempts with TanStack Query's own default exponential
    // backoff, so an ordinary transient failure (a dropped request, a
    // momentary 5xx) self-corrects within seconds without needing any
    // user activity or window refocus at all.
    retry: 3,
    // A modest, scoped override of the global client's own
    // `refetchOnWindowFocus: false` — if every retry above still failed,
    // this is the one remaining real trigger this deployment's actual
    // usage pattern (an Owner stepping away and back) can hit without
    // relying on the heartbeat, which itself needs genuine in-page
    // activity to fire at all.
    refetchOnWindowFocus: true,
  });
}
